import { useState } from 'react'
import { Component, LocaleCode, BigCommerceProductData } from '../types/component'

interface ProductCardProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

export function ProductCard({
  component,
  locale,
  storeId,
  apiUrl,
  style,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate,
}: ProductCardProps) {
  const config = component.config as any
  const product = component.bigcommerceProduct as BigCommerceProductData | null

  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  const [isInWishlist, setIsInWishlist] = useState(false)
  const [isTogglingWishlist, setIsTogglingWishlist] = useState(false)

  if (!product) {
    return (
      <div style={style} className="border rounded-lg p-4">
        <div className="text-center py-12 text-muted-foreground">
          <p>No product selected</p>
          <p className="text-xs mt-1">Select a product from the properties panel</p>
        </div>
      </div>
    )
  }

  const handleAddToCart = async () => {
    if (!storeId) return

    const lineItem = {
      product_id: Number(product.productId),
      quantity: 1,
    }

    try {
      setIsAddingToCart(true)

      let currentCartId = cartId
      if (!currentCartId) {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to create cart')
        const data = await response.json()
        currentCartId = data.data.id
      } else {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${currentCartId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to add to cart')
      }

      if (onCartUpdate && currentCartId) {
        onCartUpdate(currentCartId)
      }

      setAddedToCart(true)
      setTimeout(() => setAddedToCart(false), 2000)
    } catch (error) {
      console.error('Failed to add to cart:', error)
    } finally {
      setIsAddingToCart(false)
    }
  }

  const handleToggleWishlist = async () => {
    if (!storeId || isTogglingWishlist) return

    try {
      setIsTogglingWishlist(true)

      if (!isInWishlist) {
        let currentWishlistId = wishlistId
        if (!currentWishlistId) {
          const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: 'My Wishlist' }),
          })
          if (!response.ok) throw new Error('Failed to create wishlist')
          const data = await response.json()
          currentWishlistId = data.data.id
        }

        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists/${currentWishlistId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: Number(product.productId) }),
        })
        if (!response.ok) throw new Error('Failed to add to wishlist')

        if (onWishlistUpdate && currentWishlistId) {
          onWishlistUpdate(currentWishlistId)
        }

        setIsInWishlist(true)
      } else {
        setIsInWishlist(false)
      }
    } catch (error) {
      console.error('Failed to toggle wishlist:', error)
    } finally {
      setIsTogglingWishlist(false)
    }
  }

  return (
    <div style={style} className="border rounded-lg p-4">
      <div className="aspect-square bg-muted mb-4 rounded overflow-hidden relative">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No Image
          </div>
        )}
        {/* Wishlist Toggle */}
        {(config.showWishlistToggle as boolean) !== false && (
          <button
            onClick={handleToggleWishlist}
            disabled={isTogglingWishlist}
            className="absolute top-2 right-2 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-all disabled:opacity-50"
            aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={isInWishlist ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isInWishlist ? 'text-red-500' : 'text-gray-600'}
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
          </button>
        )}
      </div>
      <h3 className="font-semibold mb-2">{product.name}</h3>
      {product.description && (
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{product.description}</p>
      )}

      <div className="flex items-center gap-2 mb-3">
        <p className="font-bold text-lg">${product.price.toFixed(2)}</p>
        {product.salePrice && product.salePrice < product.price && (
          <p className="text-sm text-destructive line-through">${product.salePrice.toFixed(2)}</p>
        )}
      </div>
      {config.buttonText && (
        <button
          onClick={handleAddToCart}
          disabled={isAddingToCart}
          className="w-full px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isAddingToCart ? 'Adding...' : addedToCart ? 'Added!' : config.buttonText}
        </button>
      )}
    </div>
  )
}
